<template>
    <div 
        class="formfield" 
        :class="{'formfield--error': (error)}">
        <label
            v-if="label"
            class="formfield__label">
            {{ label }}
            <span
                class="required"
                v-if="required">*</span>
        </label>
        <div
            class="formfield__input formfield__location"
            v-click-outside="clickOutside">
            <div class="formfield__location__inner">
                <input
                    type="text"
                    :placeholder="placeholder"
                    @click="resolveGeoLocations"
                    @keydown.enter.prevent="keyUpEnter"
                    @keydown.up.prevent="keyUpUp"
                    @keydown.down.prevent="keyUpDown"
                    @keydown.esc.prevent="keyUpEsc"
                    @input="keyUpAll"
                    :name="name"
                    ref="currentValue"
                    autocomplete="off"
                    aria-autocomplete="list">

                <div
                    class="formfield__icon">
                    <i class="icon-location_pin" />
                </div>
            </div>

            <div 
                class="formfield__error">
                {{ error }}
            </div>

            <div
                class="autocomplete-locations"
                v-if="listVisible">
                <ul>
                    <li
                        class="autocomplete-locations__item"
                        v-for="(location, index) in geocodingLocations"
                        :key="index"
                        :class="{'autocomplete-locations__item--active': activeItemIndex === index}"
                    >
                        <div
                            class="autocomplete-locations__button"
                            @click.prevent="clickLocationItem(index)">
                            {{ location.description }}
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import { GmapsLoader } from '@/helpers';

export default {
    $_veeValidate: {
        value () {
            return this.$refs.currentValue.value;
        }
    },
    props: {
        placeholder: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        name: {
            type: String,
            default: '',
        },
        value: {
            type: Object,
            default() {
                return {}
            },
        },
        type: {
            type: String,
            default: 'text',
        },
        required: {
            type: Boolean,
            default: false,
        },
        errors: {
            type: Object,
            default: null
        },
    },
    data(){
        return {
            locationSearchInput: null,
            geocodingLocations: [],
            geoTimeout: null,
            currentResolvedString: '',
            activeItemIndex: 0,
            activeItem: null,
            listVisible: false,
            gmapsReady: false,
            datamodel: {
                lng: null,
                lat: null,
                value: null,
            },
        }
    },
    directives: {
        'click-outside': {
            bind(el, binding, vnode) {
                el.event = function (event) {
                    if (!(el == event.target || el.contains(event.target))) {
                        vnode.context[binding.expression](event);
                    }
                };

                document.body.addEventListener('click', el.event)
            },
            unbind(el) {
                document.body.removeEventListener('click', el.event)
            }
        }
    },
    created() {
        GmapsLoader.load(() => {
            this.gmapsReady = true;
        });
    },
    mounted() {
        this.$refs.currentValue.value = this.value?.value;
        this.datamodel.lng = this.value?.lng;
        this.datamodel.lat = this.value?.lat;
        this.datamodel.value = this.value?.value;
    },
    computed: {
        error() {
            if(this.errors) {
                return this.errors.first(this.name);
            }

            return null;
        },
    },
    watch: {
        activeItemIndex() {
            if(this.geocodingLocations.length !== 0) {
                if (this.activeItemIndex < 0) {
                    this.activeItemIndex = this.geocodingLocations.length - 1;
                } else if (this.activeItemIndex > this.geocodingLocations.length - 1) {
                    this.activeItemIndex = 0;
                }

                const selectedLocation = this.geocodingLocations[this.activeItemIndex];
                if(selectedLocation && selectedLocation.description) {
                    this.setActiveItem(selectedLocation);
                }
            }
        },
        datamodel: {
            deep: true,
            handler() {
                this.$emit('input', this.datamodel);
            }
        },
        value: {
            deep: true,
            handler() {
                this.$refs.currentValue.value = this.value?.value || '';
                this.datamodel.lng = this.value?.lng;
                this.datamodel.lat = this.value?.lat;
                this.datamodel.value = this.value?.value;
            }
        }
    },
    methods: {
        keyUpUp() {
            if(this.activeItemIndex === null) {
                this.activeItemIndex = 0;
            } else {
                this.activeItemIndex -= 1;
            }
        },
        keyUpDown() {
            if(this.activeItemIndex === null) {
                this.activeItemIndex = 0;
            } else {
                this.activeItemIndex += 1;
            }
        },
        keyUpEsc() {
            this.hideAutoCompleteList();
            this.unfocusField();
        },
        keyUpEnter() {
            this.hideAutoCompleteList();
            this.unfocusField();
        },
        clickOutside() {
            this.hideAutoCompleteList();
        },
        keyUpAll(e) {
            this.$refs.currentValue.value = this.$refs.currentValue.value;

            // key up, down, enter, esc
            const disabledKeyCodes = [40, 38, 13, 27];

            if(disabledKeyCodes.indexOf(e.keyCode) === -1) {
                if(this.$refs.currentValue.value === '') {
                    this.geocodingLocations = [];
                    this.setActiveItem();
                } else {
                    this.activeItemIndex = null;

                    if(this.$refs.currentValue.value != this.currentResolvedString) {
                        this.geocodingLocations = [];
                    }

                    clearTimeout(this.geoTimeout);
                    this.geoTimeout = setTimeout(() => {
                        this.resolveGeoLocations();
                    }, 500);
                }
            }
        },
        resolveGeoLocations() {
            if(!this.gmapsReady) {
                return;
            }
            
            this.listVisible = true;

            this.currentResolvedString = this.$refs.currentValue.value;

            if(!this.$refs.currentValue.value) {
                this.geocodingLocations = [];
            }
            else {
                this.activeItemIndex = null;
                const AutocompleteService = new window.google.maps.places.AutocompleteService();
                AutocompleteService.getPlacePredictions({ input: this.$refs.currentValue.value,  types: ['geocode'], componentRestrictions: {country: 'nl'} }, (results) => {
                    if(!results) {
                        this.geocodingLocations = [];
                    } else {
                        this.geocodingLocations = results;
                    }

                    if(this.geocodingLocations.length === 0) {
                        this.setActiveItem();
                    }
                });
            }
        },
        clickLocationItem(index) {
            this.activeItemIndex = index;
            this.hideAutoCompleteList();
        },
        setActiveItem(value) {
            this.activeItem = value;

            if(this.activeItem && this.activeItem.place_id) {
                this.$refs.currentValue.value = this.currentResolvedString = value.description;

                const geocoder = new window.google.maps.Geocoder;
                geocoder.geocode({'placeId': this.activeItem.place_id}, (results) => {
                    if(results[0]) {
                        this.datamodel.lng = results[0].geometry.location.lng();
                        this.datamodel.lat = results[0].geometry.location.lat();
                        this.datamodel.value = this.activeItem.description;
                    }
                    else {
                        this.resetDatamodel();
                    }
                });
            } else {
                this.resetDatamodel();
            }
        },
        resetDatamodel() {
            this.datamodel.lng = null;
            this.datamodel.lat = null;
            this.datamodel.value = null;
        },
        hideAutoCompleteList() {
            clearTimeout(this.geoTimeout);

            if(this.geocodingLocations.length > 0) {
                this.activeItem = this.geocodingLocations[this.activeItemIndex || 0];
                this.setActiveItem(this.activeItem);
            }

            this.geocodingLocations = [];
            this.listVisible = false;
        },
        showAutoCompleteList() {
            if(this.$refs.currentValue.value && this.geocodingLocations.length > 0) {
                this.listVisible = true;
            }
        },
        unfocusField() {
            const tmpInputField = document.createElement("input");
            tmpInputField.classList.add('unfocusfield');
            this.$el.appendChild(tmpInputField);
            tmpInputField.focus();
            this.$el.removeChild(tmpInputField);
        }
    },
    destroyed() {
        clearTimeout(this.geoTimeout);
    },    
};
</script>
